import React from 'react';
import { useTranslation } from 'react-i18next';
import { createSelector } from '@reduxjs/toolkit';
import { generatePath, useHistory } from 'react-router-dom';

import type { ILatestDatabaseItem } from '@/api/types';

import {
  LoaderWrapper,
  ResourceTableCell,
  ResourceTableHeader,
  ResourceTableHeaderCell,
  ResourceTableRow,
  Table,
} from '@/components/common';
import { ChevronRight } from '@/assets/icons';
import { useAppSelector, useTimezone } from '@/hooks';
import { getLatestDatabaseItems } from '@/store/slices/resourcesSlice';
import { MONTH_DAY_YEAR_FORMAT } from '@/config/variables';
import { ButtonPreview, Container, HeaderContainer, Title } from './ResourcesItemsCard.styled';
import { selectPendingAction } from '@/store/selectors/resourcesSelectors';
import { ResourceDatabaseItemsCardSkeleton } from './ResourcesDatabaseItemsCard.skeleton';
import { StyledNoData } from './ResourcesDatabaseItemsCard.styles';
import routes from '@/config/routes';

const resourcesDatabaseItemsCardSelector = createSelector(
  selectPendingAction(getLatestDatabaseItems.typePrefix),
  isFetching => ({
    isFetching,
  }),
);
interface IProps {
  title: string;
  noDataLabel: string;
  iconSrc: string;
  button: React.ReactNode;
  items: ILatestDatabaseItem[];
}

const ResourcesDatabaseItemsCard = ({ title, iconSrc, button, items, noDataLabel }: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const { formatDateTime } = useTimezone();
  const { isFetching } = useAppSelector(resourcesDatabaseItemsCardSelector);
  const history = useHistory();

  return (
    <Container>
      <HeaderContainer>
        <Title>
          <img src={iconSrc} />
          <h4>{title}</h4>
        </Title>
        {button}
      </HeaderContainer>
      <LoaderWrapper isLoading={isFetching} loader={<ResourceDatabaseItemsCardSkeleton />}>
        {!items.some(item => item.latestTimestamp) ? (
          <StyledNoData label={noDataLabel} />
        ) : (
          <Table>
            <ResourceTableHeader className="small">
              <ResourceTableHeaderCell size={2}>{t`resources.foldersList.headerName`}</ResourceTableHeaderCell>
              <ResourceTableHeaderCell size={1}>{t`resources.foldersList.headerLatestChange`}</ResourceTableHeaderCell>
              <ResourceTableHeaderCell width="12px" minWidth="12px" className="p-0 ms-3"></ResourceTableHeaderCell>
            </ResourceTableHeader>
            {items
              .filter(item => item.latestTimestamp && item.newItemCount)
              .map(item => (
                <ResourceTableRow className="small" key={item.databaseId}>
                  <ResourceTableCell size={2}>{item.databaseName}</ResourceTableCell>
                  <ResourceTableCell size={1}>
                    {formatDateTime(item.latestTimestamp, MONTH_DAY_YEAR_FORMAT)}
                  </ResourceTableCell>
                  <ResourceTableCell width="12px" minWidth="12px">
                    <ButtonPreview
                      src={ChevronRight}
                      onClick={() => {
                        history.push(
                          generatePath(routes.sp.scheduling.resources.database, {
                            client: item.clientId,
                            dbId: item.databaseId,
                          }),
                        );
                      }}
                    />
                  </ResourceTableCell>
                </ResourceTableRow>
              ))}
          </Table>
        )}
      </LoaderWrapper>
    </Container>
  );
};

export { ResourcesDatabaseItemsCard };
